import { createEnvelopeSource } from './create-envelope-source';

/**
 * Onboarding-state KPI source. Pure config over the standardized envelope
 * (`GET /analytics/onboarding-state`): `registered` is the scalar count of
 * accounts created in the selected period, and `profileCompleted` /
 * `phoneVerified` / `ageVerified` / `segmentation` / `permission` / `complete`
 * are scalar counts of that same cohort currently sitting in each state. All
 * seven are scalars; there is no `conversionRate`. All mapping and fetching
 * lives in `createEnvelopeSource`.
 *
 * The `bars` form renders the six states as six labeled bars via
 * `barsMetrics` (mirrors the `columnsMetrics` mechanism used by
 * coupon-activation's columns card), read directly by metric id rather than
 * through a dimensional breakdown axis. `barsBaseMetricId: 'registered'`
 * shares one denominator across all six, so each bar is a share of the
 * period's registered accounts, not of whichever configured bar happens to
 * be tallest. These are independent states of the cohort today, not an
 * ordered funnel: `complete` can exceed `segmentation` because a skipped
 * optional step still lets an account finish onboarding. A state whose
 * source table postdates the requested window arrives as a null scalar and
 * renders its bar unavailable (see `create-envelope-source`), the rest of
 * the bars intact; `registered` itself is load-bearing for every bar, so a
 * missing, non-scalar, null, or zero `registered` errors the whole card
 * instead.
 *
 * The backend emits no warning for this KPI.
 *
 * Two forms over the same seven metrics. `bars` reads the six states as
 * shares of `registered` (horizontal, base in the card title). `columns`
 * reads `registered` plus the six states as absolute counts in a vertical
 * chart, deliberately WITHOUT the form's step-over-step delta: the states are
 * not nested (a skipped optional step still completes onboarding, and age
 * verification is a standalone activation flow), so a step delta would assert
 * a sequence the data does not have. `columnsMetrics` carries no delta, which
 * is exactly the shape wanted here.
 */
export const onboardingStateSource = createEnvelopeSource({
    label: 'Onboarding state',
    endpoint: 'analytics/onboarding-state',
    metrics: [
        { id: 'registered', label: 'Registered', type: 'scalar' },
        { id: 'profileCompleted', label: 'Profile completed', type: 'scalar' },
        { id: 'phoneVerified', label: 'Phone verified', type: 'scalar' },
        { id: 'ageVerified', label: 'Age verified', type: 'scalar' },
        { id: 'segmentation', label: 'Segmentation', type: 'scalar' },
        { id: 'permission', label: 'Permission', type: 'scalar' },
        { id: 'complete', label: 'Complete', type: 'scalar' },
    ],
    forms: ['bars', 'columns'],
    barsMetrics: [
        { label: 'Profile completed', metricId: 'profileCompleted' },
        { label: 'Phone verified', metricId: 'phoneVerified' },
        { label: 'Age verified', metricId: 'ageVerified' },
        { label: 'Segmentation', metricId: 'segmentation' },
        { label: 'Permission', metricId: 'permission' },
        { label: 'Complete', metricId: 'complete' },
    ],
    barsBaseMetricId: 'registered',
    columnsBaseMetricId: 'registered',
    columnsMetrics: [
        { label: 'Registration', metricId: 'registered' },
        { label: 'Profile completed', metricId: 'profileCompleted' },
        { label: 'Phone verified', metricId: 'phoneVerified' },
        { label: 'Age verified', metricId: 'ageVerified' },
        { label: 'Segmentation', metricId: 'segmentation' },
        { label: 'Permission', metricId: 'permission' },
        { label: 'Complete', metricId: 'complete' },
    ],
    regionGrain: 'national',
});
